'use client';

import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { fNumber } from 'src/utils/format/format-number';

import { LINE, RADIUS } from 'src/theme/ui-tokens';

/**
 * **낱말 구름** — 값이 큰 낱말일수록 크고 진하게, 값은 도움말로 보인다.
 *
 * 🔑 순위 막대를 한 칸에 셋씩 쌓으면 세 표가 똑같이 생겨 눈이 미끄러진다 — 「무엇이 많이 나오나」만
 *   알면 되는 축(성분·피부 고민)은 **막대 대신 낱말 크기**로 읽는 편이 빠르고 자리도 덜 먹는다.
 * ⚠️ 크기는 **1등 대비 비율**이다 — 전체 대비가 아니다. 1등이 작으면 모두 작아지는 것이 맞다.
 * ⚠️ **알약 높이는 고정**이고 글자 크기만 달라진다 — 높이까지 달라지면 줄이 울퉁불퉁해 읽기 어렵다.
 * ⚠️ 값 자체는 낱말에 적지 않는다(마우스를 올리면 나온다) — 숫자를 함께 적으면 다시 표가 된다.
 */
export function HubTagCloud({
  title,
  rows,
  max = 14,
  action,
  onPick,
  marked,
}: {
  /** 안 주면 제목 줄을 그리지 않는다 — 구역 카드가 이름을 이미 쥐고 있을 때다 */
  title?: string;
  rows: { name?: string; label?: string; count?: number }[];
  max?: number;
  /** 제목 줄 오른쪽에 붙는 것 — ⚠️ **다루는 것 옆에 둔다**(섹션 머리에 두면 구름까지 내려오면 안 보인다) */
  action?: ReactNode;
  /** 알약을 눌렀을 때 — 없으면 **누를 수 없는 표시**다(손짓도 안 준다) */
  onPick?: (name: string) => void;
  /** 지금 걸려 있는 이름(소문자) — 테두리를 진하게 해서 알린다 */
  marked?: Set<string>;
}) {
  const shown = rows
    .slice(0, max)
    .map((row) => ({ name: String(row.name ?? row.label ?? ''), count: Number(row.count ?? 0) }))
    .filter((row) => row.name);
  const top = shown[0]?.count ?? 0;

  return (
    <Box>
      {(title || action) && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.875 }}>
          <Typography variant="uiLabel" color="text.disabled" sx={{ flex: 1, minWidth: 0 }}>{title ?? ''}</Typography>
          {action}
        </Stack>
      )}
      {shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary">값이 없습니다.</Typography>
      ) : (
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.625 }}>
          {shown.map((row) => {
            const ratio = top > 0 ? row.count / top : 0;
            const picked = marked?.has(row.name.toLowerCase()) ?? false;
            return (
              <Tooltip key={row.name} title={`${fNumber(row.count)}건`} placement="top" disableInteractive>
                <Box
                  component="span"
                  role={onPick ? 'button' : undefined}
                  tabIndex={onPick ? 0 : undefined}
                  onClick={onPick ? () => onPick(row.name) : undefined}
                  onKeyDown={onPick
                    ? (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(row.name); }
                    }
                    : undefined}
                  sx={{
                    // 🚨 **높이를 못 박고 flex 로 가운데 둔다** — 글자 크기가 칩마다 다른데 inline 박스로 두면
                    //    글줄 높이가 따라 달라져 알약 높이가 제각각이 되고 글자도 위아래로 밀린다.
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: CHIP_H,
                    px: 1.125,
                    borderRadius: RADIUS.pill,
                    whiteSpace: 'nowrap',
                    border: '1px solid',
                    // 클수록 진하다 — 셋으로만 갈라 색이 연속으로 흐려지지 않게 한다
                    ...toneOf(ratio),
                    fontSize: 12 + Math.round(ratio * 3),
                    fontWeight: 600,
                    lineHeight: 1,
                    // 걸린 것은 테두리로 알린다 — 색을 바꾸면 「크다」는 뜻과 섞인다
                    ...(picked ? { borderColor: 'primary.main', borderWidth: 2, px: 1.0625 } : {}),
                    ...(onPick ? { cursor: 'pointer', '&:hover': { borderColor: 'primary.light' } } : {}),
                  }}
                >
                  {row.name}
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

/**
 * 알약 높이(px) — **글자 크기와 무관하게 고정**이다.
 * ⚠️ 높이를 글줄에 맡기면 큰 글자 알약만 커져 줄이 울퉁불퉁해진다. 크기 차이는 **글자와 색**으로만 준다.
 */
const CHIP_H = 30;

/** 색은 세 켜뿐이다 — 값마다 다른 농도를 주면 무엇이 큰지 오히려 안 읽힌다 */
function toneOf(ratio: number) {
  if (ratio > 0.5) return { bgcolor: 'primary.main', borderColor: 'primary.main', color: 'background.paper' };
  if (ratio > 0.2) return { bgcolor: 'primary.lighter', borderColor: 'primary.lighter', color: 'primary.dark' };
  return { bgcolor: 'background.paper', borderColor: LINE.edge, color: 'text.secondary' };
}
